import {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  LoadingOutlined,
  SearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Col,
  Input,
  message,
  Row,
  Space,
  Spin,
  Typography,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { Link, useHistory, useParams } from 'react-router-dom';

import { ApiError, handleError } from '../../../api/base';
import { useAuth } from '../../../hooks/useAuth';
import useDebounce from '../../../hooks/useDebounce';
import { ClientModel } from '../../../models/client';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  getClient,
  paginateClientUsers,
  searchClientUsersByName,
  updateClient,
} from '../../../store/features/clients/clientsSlice';
import { UserPermissions } from '../../../util/user';
import { DrawerHashRoute } from '../../containers/Drawers/types';
import ViewWrapper from '../../elements/ViewWrapper';
import ClientUsersTable from './UserTable';

import './ClientView.less';

const { Title, Text } = Typography;
const antSpinIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

export interface ClientModelWithExtensions extends ClientModel {
  email_notification: string;
  markets: string;
  notes: string;
  client_type: string;
  default_market: string;
  library_path_3d: string | null;
}

const ClientView = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState('');

  const debouncedSearchQuery = useDebounce<string>(searchQuery, 500);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { id: clientId } = useParams<{ id: string }>();

  const {
    client,
    users,
    fetchingClient,
    searchingUsers,
    updatingClient,
    usersPagination,
  } = useAppSelector((state) => state.clients);

  const { value: usersState } = useAppSelector((state) => state.users);

  const markets = useAppSelector(
    (state) => state.customFields.customField?.options || []
  );

  const clientWithExtensions = client as ClientModelWithExtensions;

  const defaultMarket = client?.default_market;
  const marketName =
    markets.find((market) => market.id === Number(defaultMarket))?.name || '-';

  // derived flags removed with pagination

  const { hasPermission } = useAuth();

  const canEditClient = hasPermission([
    UserPermissions.ClientsEdit,
    UserPermissions.ClientsCreate,
  ]);

  // removed infinite load-more in favor of pagination

  // Fetch client only when clientId changes; avoid duplicate mount + id change calls
  const fetchedClientIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!clientId) return;
    if (fetchedClientIdRef.current === clientId) return; // avoid duplicate fetch on mount+id effect
    fetchedClientIdRef.current = clientId;
    dispatch(getClient({ id: clientId }));
  }, [clientId, dispatch]);

  const onSuccess = useCallback(() => {
    message.success('Notes saved.');
    setEditing(false);
  }, []);

  const onError = useCallback((err: ApiError) => {
    handleError(err);
  }, []);

  const handleEditNotes = () => {
    setEditing(true);
  };

  const handleSaveNotes = useCallback(
    async (notes: string) => {
      try {
        if (client) {
          const short_name = client?.short_name;
          const updatedClient = {
            short_name: short_name!,
            fields: {
              client_notes: notes,
            },
          };

          await dispatch(
            updateClient({
              clientId,
              client: updatedClient,
            })
          );
          onSuccess();
        }
      } catch (error) {
        onError(error as ApiError);
      }
    },
    [client, clientId, dispatch, onError, onSuccess]
  );

  const handleTextAreaChange = (e: {
    target: { value: SetStateAction<string> };
  }) => {
    setNotes(e.target.value);
  };

  const updateSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  useEffect(() => {
    const id = clientId || client?.uuid;
    if (id) {
      dispatch(
        searchClientUsersByName({
          id,
          query: debouncedSearchQuery,
        })
      );
    }
  }, [
    usersState,
    client?.uuid,
    clientId,
    debouncedSearchQuery,
    dispatch,
    fetchingClient,
  ]);

  if (fetchingClient)
    return (
      <div className="client-view-container">
        <Spin />
      </div>
    );

  const clientInfoSection = hasPermission([
    UserPermissions.ClientsEdit,
    UserPermissions.ClientsCreate,
    UserPermissions.ClientsView,
    UserPermissions.ClientsDelete,
  ]) ? (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <div className="card">
          <Space direction="vertical">
            <Title level={5}>Notes</Title>
            {clientWithExtensions?.notes && !editing ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div className="notes-container">
                  <Text type="secondary">{clientWithExtensions?.notes}</Text>
                </div>
                <Button type="primary" onClick={handleEditNotes}>
                  Edit Notes
                </Button>
              </Space>
            ) : (
              <>
                <TextArea
                  autoSize={{ minRows: 4, maxRows: 4 }}
                  placeholder={
                    clientWithExtensions?.notes
                      ? (clientWithExtensions?.notes as string)
                      : 'Add client notes here.'
                  }
                  value={notes}
                  onChange={handleTextAreaChange}
                />
                <div className="buttons-container">
                  <Button
                    type="primary"
                    loading={updatingClient}
                    disabled={updatingClient}
                    onClick={() => handleSaveNotes(notes)}
                  >
                    Save Notes
                  </Button>
                  {clientWithExtensions?.notes && (
                    <Button onClick={() => setEditing(false)}>Cancel</Button>
                  )}
                </div>
              </>
            )}
          </Space>
        </div>
      </Col>
    </Row>
  ) : null;

  return (
    <ViewWrapper
      headerTitle={<Title level={4}>{clientWithExtensions?.name}</Title>}
      headerSectionRight={
        canEditClient && (
          <Button
            type="primary"
            onClick={() =>
              history.push({
                hash: DrawerHashRoute.ClientForm,
                state: {
                  client,
                },
              })
            }
          >
            Edit
          </Button>
        )
      }
      options={{ goBack: true }}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <div className="card">
            <Title level={5}>General Info</Title>
            <Text type="secondary" className="head">
              Name
            </Text>
            <Text className="body">{client?.name}</Text>
            <Row gutter={[16, 16]}>
              <Col span={12} className="flex-column">
                <Text type="secondary" className="head">
                  Date Created
                </Text>
                <Text className="body">{client?.created_at}</Text>
              </Col>
              <Col span={12} className="flex-column">
                <Text type="secondary" className="head">
                  Client Identifier/URL
                </Text>
                <Text className="body">{client?.short_name}</Text>
              </Col>
            </Row>
            <Text type="secondary" className="head">
              Client Type
            </Text>
            <Text className="body">{clientWithExtensions?.client_type}</Text>
            <Text type="secondary" className="head">
              Default Market
            </Text>
            <Text className="body">{marketName}</Text>
            <Text type="secondary" className="head">
              2D Library Path
            </Text>
            <Text className="body">{client?.library_path_2d || '-'}</Text>
            <Text type="secondary" className="head">
              Studio License Key
            </Text>
            <Text className="body">{client?.studio_license_key || '-'}</Text>
          </div>
        </Col>
        <Col span={12}>{clientInfoSection}</Col>

        <Col span={24}>
          <div className="card">
            <div className="search-bar">
              <div className="search-bar-left">
                <Space direction="horizontal">
                  <Title level={5} className="title">
                    {client?.name} Users
                  </Title>
                  <Badge
                    size="default"
                    count={usersPagination?.count || 0}
                    className="total-count-badge"
                  />
                </Space>
              </div>
              <div className="search-bar-right">
                <Space direction="horizontal" size="middle">
                  <Input
                    allowClear={!searchingUsers}
                    value={searchQuery}
                    onChange={(e) => updateSearchQuery(e.target.value)}
                    placeholder="Search for a user..."
                    prefix={
                      <SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    }
                    suffix={searchingUsers && <Spin indicator={antSpinIcon} />}
                  />
                  {hasPermission(UserPermissions.UsersCreate) ? (
                    <Link to={DrawerHashRoute.UserForm}>
                      <Button type="primary">
                        Create New User <UserAddOutlined />
                      </Button>
                    </Link>
                  ) : (
                    <div />
                  )}
                </Space>
              </div>
            </div>
            {client && (
              <ClientUsersTable
                history={history}
                loading={searchingUsers}
                users={users}
                page={
                  Math.floor(
                    (usersPagination?.offset || 0) /
                      (usersPagination?.limit || 10)
                  ) + 1
                }
                pageSize={usersPagination?.limit || 10}
                total={usersPagination?.count || 0}
                onPaginate={(nextPage, nextPageSize, sort) =>
                  dispatch(
                    paginateClientUsers({
                      clientId: clientWithExtensions.uuid,
                      page: nextPage,
                      pageSize: nextPageSize,
                      queryStr: searchQuery,
                      sort,
                    })
                  )
                }
              />
            )}
          </div>
        </Col>
      </Row>
    </ViewWrapper>
  );
};

export default ClientView;
